/**
 * Passage-plan conductor. The assisted "Plan a passage" flow is not a separate
 * copy of the tools: it is a thin sequencer that walks the SAME shared tools in
 * order. This store just tracks where we are in that sequence and whether it is
 * running, paused, or idle.
 *
 * Jump-out behaviour (agreed): if the user steps out to use a tool free-form
 * while a plan is active, the plan pauses (keeping its place) and offers to
 * resume, rather than dissolving. Exiting clears it.
 */
import { create } from 'zustand';

export type PlanStepId =
  | 'conditions'
  | 'destination'
  | 'route'
  | 'check'
  | 'pilotage'
  | 'follow';

export interface PlanStep {
  id: PlanStepId;
  label: string;
  /** One-line prompt shown while on this step. */
  hint: string;
}

/** The assisted sequence, in order. */
export const PLAN_STEPS: PlanStep[] = [
  { id: 'conditions', label: 'Conditions', hint: 'Check the weather, tide and streams for your window.' },
  { id: 'destination', label: 'Destination', hint: 'Tap the chart and choose Set as destination.' },
  { id: 'route', label: 'Route', hint: 'Plot a route to the destination, manually or automatically.' },
  { id: 'check', label: 'Check', hint: 'Zoom in along the route and review any hazards flagged.' },
  { id: 'pilotage', label: 'Pilotage', hint: 'Make pilotage plans for any close-quarters sections.' },
  { id: 'follow', label: 'Follow', hint: 'Follow the route now, or set a reminder for departure.' },
];

export type PlanStatus = 'idle' | 'active' | 'paused';

interface PassagePlanState {
  status: PlanStatus;
  /** Index into PLAN_STEPS. Meaningful while status is active or paused. */
  stepIndex: number;

  /** Begin the assisted flow at the first step. */
  start: () => void;
  /** Advance a step; finishing past the last step ends the plan. */
  next: () => void;
  /** Step back (clamped at the first step). */
  back: () => void;
  /** Jump to a specific step (clamped to range), keeping the plan active. */
  goToStep: (index: number) => void;
  /** Pause the plan, keeping its place (used when the user goes free-form). */
  pause: () => void;
  /** Resume a paused plan. */
  resume: () => void;
  /** Abandon the plan and reset. */
  exit: () => void;
  /** Complete the plan (reached the end) and reset to idle. */
  finish: () => void;
}

const LAST = PLAN_STEPS.length - 1;
const clamp = (i: number) => Math.max(0, Math.min(LAST, i));

export const usePassagePlanStore = create<PassagePlanState>((set, get) => ({
  status: 'idle',
  stepIndex: 0,

  start: () => set({ status: 'active', stepIndex: 0 }),
  next: () => {
    const i = get().stepIndex;
    if (i >= LAST) {
      set({ status: 'idle', stepIndex: 0 });
      return;
    }
    set({ stepIndex: i + 1, status: 'active' });
  },
  back: () => set((s) => ({ stepIndex: clamp(s.stepIndex - 1) })),
  goToStep: (index) => set({ stepIndex: clamp(index), status: 'active' }),
  pause: () => set((s) => (s.status === 'active' ? { status: 'paused' } : {})),
  resume: () => set((s) => (s.status === 'paused' ? { status: 'active' } : {})),
  exit: () => set({ status: 'idle', stepIndex: 0 }),
  finish: () => set({ status: 'idle', stepIndex: 0 }),
}));

/** The current step object (or the first step when idle), for the UI. */
export function currentStep(state: Pick<PassagePlanState, 'stepIndex'>): PlanStep {
  return PLAN_STEPS[clamp(state.stepIndex)];
}
